import React from 'react';
import { X, ChevronUp, ChevronDown, Wrench, CheckCircle, Eye, Settings, ListTodo, Brain, FileOutput, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { RunTab } from './tabs/RunTab';
import { FeedbackTab } from './tabs/FeedbackTab';
import { MetadataTab } from './tabs/MetadataTab';
import type { TraceItem, TraceItemType } from '@/types/trace';
import { findTraceItemById, flattenTraceItems, formatDuration, formatTokens } from '@/utils/traceTransformer';

const iconMap: Record<TraceItemType, React.ElementType> = {
  setup: Settings,
  task: ListTodo,
  tool_call: Wrench,
  tool_result: CheckCircle,
  observation: Eye,
  thinking: Brain,
  final_output: FileOutput,
  synthesis: GitMerge,
};

const colorMap: Record<TraceItemType, string> = {
  setup: 'text-slate-500',
  task: 'text-blue-500',
  tool_call: 'text-amber-500',
  tool_result: 'text-green-500',
  observation: 'text-purple-500',
  thinking: 'text-pink-500',
  final_output: 'text-emerald-500',
  synthesis: 'text-cyan-500',
};

interface TraceDetailPanelProps {
  items: TraceItem[];
  className?: string;
}

export function TraceDetailPanel({ items, className }: TraceDetailPanelProps) {
  const {
    state,
    setDetailPanelOpen,
    setDetailTab,
    selectTrace,
  } = useTraceSidebar();

  // Find the selected item
  const selectedItem = state.selectedTraceId
    ? findTraceItemById(items, state.selectedTraceId)
    : null;

  // Get flat list for navigation
  const flatItems = React.useMemo(() => flattenTraceItems(items), [items]);

  // Navigate to previous/next item
  const handleNavigate = (direction: 'up' | 'down') => {
    if (!state.selectedTraceId) return;

    const currentIndex = flatItems.findIndex(item => item.id === state.selectedTraceId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < flatItems.length) {
      selectTrace(flatItems[newIndex].id);
    }
  };

  if (!state.detailPanelOpen || !selectedItem) {
    return null;
  }

  const Icon = iconMap[selectedItem.type] || Eye;
  const iconColor = colorMap[selectedItem.type] || 'text-gray-500';
  const currentIndex = flatItems.findIndex(i => i.id === state.selectedTraceId);

  return (
    <div className={`flex flex-col h-full bg-background ${className || ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-card">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
          <div className="min-w-0">
            <h2 className="font-semibold text-lg truncate">
              {selectedItem.displayName}
            </h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge variant={selectedItem.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                {selectedItem.status}
              </Badge>
              {selectedItem.duration !== null && (
                <span className="text-xs text-muted-foreground">
                  {formatDuration(selectedItem.duration)}
                </span>
              )}
              {selectedItem.tokenCount.total > 0 && (
                <Badge variant="outline" className="text-xs">
                  {formatTokens(selectedItem.tokenCount.total)} tokens
                </Badge>
              )}
              {selectedItem.toolName && (
                <span className="text-xs text-muted-foreground font-mono">
                  {selectedItem.toolName}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Navigation */}
          <span className="text-xs text-muted-foreground mr-2">
            {currentIndex + 1} / {flatItems.length}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleNavigate('up')}
            disabled={currentIndex === 0}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleNavigate('down')}
            disabled={currentIndex === flatItems.length - 1}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-6 mx-2" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              selectTrace(null);
              setDetailPanelOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={state.detailPanelTab}
        onValueChange={(v) => setDetailTab(v as 'run' | 'feedback' | 'metadata')}
        className="flex-1 flex flex-col overflow-hidden"
      >
        <TabsList className="w-full justify-start rounded-none border-b bg-card h-10 p-0 px-6">
          <TabsTrigger
            value="run"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent h-10 px-4"
          >
            Run
          </TabsTrigger>
          <TabsTrigger
            value="feedback"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent h-10 px-4"
          >
            Feedback
          </TabsTrigger>
          <TabsTrigger
            value="metadata"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent h-10 px-4"
          >
            Metadata
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-auto">
          <TabsContent value="run" className="h-full m-0 p-6">
            <RunTab item={selectedItem} />
          </TabsContent>
          <TabsContent value="feedback" className="h-full m-0 p-6">
            <FeedbackTab item={selectedItem} />
          </TabsContent>
          <TabsContent value="metadata" className="h-full m-0 p-6">
            <MetadataTab item={selectedItem} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

export default TraceDetailPanel;
